import { tracker } from 'app-components/utils/core';
import _isEqual from 'lodash/isEqual';
import { useCallback } from 'react';

import { mkFilterObject } from 'app-components/flux/filters/filtersTypes';
import { usePreferenceV2 } from 'app-components/flux/preferences-v2';
import type { DisplayOptionsType } from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import type { ReportAction } from 'containers/NewReports/types';
import {
  RESET_DISPLAY_OPTIONS,
  type ReportsState,
  UPDATE_ACCOUNTING_TYPE,
  UPDATE_BREADCRUMBS,
  UPDATE_CHART_TYPE,
  UPDATE_DATE_RANGE,
  UPDATE_DISPLAY_OPTIONS,
  UPDATE_FILTER,
  UPDATE_GROUP_BY,
  UPDATE_LOADING,
  UPDATE_OPTIONS,
  UPDATE_OVERTIME,
  UPDATE_REPORT_SUBTYPE,
  UPDATE_REPORT_TYPE,
  UPDATE_SEGMENT_BY,
  UPDATE_TAB_NAME,
  UPDATE_TIMESTEP,
  UPDATE_USAGE,
} from 'containers/NewReports/types';

const stableDefault = {};

const useStatesHelper = ({ state, dispatch }: { state: ReportsState; dispatch: React.Dispatch<ReportAction> }) => {
  const { tabName, displayOptions /* groupBy, timestep, usage */ } = state;
  const [_preferencesDisplayOptions, setPreferencesDisplayOptions] = usePreferenceV2(
    'report-grid-display-options',
    stableDefault,
  );

  // Helper to set display options. Will set preference and let preferences update state
  //   UI => setDisplayOptions => setPreferencesOptions => dispatch to state
  const setDisplayOptions = useCallback(
    (values: DisplayOptionsType) => {
      const newOptions = { ...displayOptions, ...values };
      if (!_isEqual(state.displayOptions, newOptions)) {
        tracker.track(tracker.events.changeGridDisplayOption, {
          changed_value: values,
          display_option: newOptions,
          report_title: tabName,
        });
        setPreferencesDisplayOptions(newOptions);
      }
      return newOptions;
    },
    [displayOptions, setPreferencesDisplayOptions, state.displayOptions, tabName],
  );

  return useCallback(
    (setting: any, value: any) => {
      let updatedState;

      switch (setting) {
        case 'breadcrumbs':
          dispatch({ type: UPDATE_BREADCRUMBS, payload: { breadcrumbs: value } });
          break;
        case 'chartType':
          dispatch({ type: UPDATE_CHART_TYPE, payload: { chartType: value } });
          break;
        case 'dateRange':
          dispatch({ type: UPDATE_DATE_RANGE, payload: { dateRange: { id: value.id, interval: value.interval } } });
          break;
        case 'loading':
          dispatch({ type: UPDATE_LOADING, payload: { loading: value === 'true' } });
          break;
        case 'usage':
          dispatch({ type: UPDATE_USAGE, payload: { usage: value } });
          break;
        case 'reportSubType':
          dispatch({ type: UPDATE_REPORT_SUBTYPE, payload: { reportSubtype: value } });
          break;
        case 'reportType':
          dispatch({ type: UPDATE_REPORT_TYPE, payload: { reportType: value } });
          break;
        case 'filter': {
          const newFilter = value || mkFilterObject({ id: state.filter.id });
          dispatch({ type: UPDATE_FILTER, payload: { filter: newFilter } });
          break;
        }
        case 'options':
          dispatch({ type: UPDATE_OPTIONS, payload: { options: value } });
          break;
        case 'accountingType':
          dispatch({ type: UPDATE_ACCOUNTING_TYPE, payload: { accountingType: value } });
          break;
        case 'tabName':
          dispatch({ type: UPDATE_TAB_NAME, payload: { tabName: value } });
          break;
        case 'displayOptions': {
          updatedState = { ...state, displayOptions: { ...state.displayOptions, ...value } };
          const newOptions = setDisplayOptions(updatedState.displayOptions);
          dispatch({ type: UPDATE_DISPLAY_OPTIONS, payload: { displayOptions: newOptions } });
          break;
        }
        case 'resetDisplayOptions': {
          updatedState = { ...state, displayOptions: { ...state.displayOptions, ...value } };
          const newOptions = setDisplayOptions(updatedState.displayOptions);
          dispatch({ type: RESET_DISPLAY_OPTIONS, payload: { displayOptions: newOptions, accountingType: 'CASH' } });
          break;
        }
        case 'timestep':
          dispatch({ type: UPDATE_TIMESTEP, payload: { timestep: value } });
          break;
        case 'segmentBy':
          dispatch({ type: UPDATE_SEGMENT_BY, payload: { segmentBy: value } });
          break;
        case 'groupBy':
          dispatch({ type: UPDATE_GROUP_BY, payload: { groupBy: value } });
          break;
        case 'overTime':
          dispatch({ type: UPDATE_OVERTIME, payload: { overTime: value } });
          break;
        default:
          break;
      }
    },
    [dispatch, state, setDisplayOptions],
  );
};

export default useStatesHelper;
